import { useEffect, useState, useRef, useCallback } from 'react'
import { Tree, ITreeNode } from '@blueprintjs/core'
import { cloneDeep, isEmpty } from 'lodash'
import { IPortfolioRegion } from '@api'
import {
  mapLocationTreeContents,
  forEachNode,
  forEachParenNode,
  LocationTreeNodeT,
  LocationNodeType,
} from './utils'
import makeSelectEachNodeByFloorWorkdayTime from './utils/makeSelectEachNodeByFloorWorkdayTime'

type UseSelectedLocationArgs = {
  regions: IPortfolioRegion[]
  selectedFloorIds: string[]
}

const useSelectedLocationTreeContents = ({
  regions,
  selectedFloorIds,
}: UseSelectedLocationArgs) => {
  const [selectedLocationTreeNodes, _setSelectedLocationTreeNodesState] = useState<
    LocationTreeNodeT[]
  >([])
  const isLocationTreeNodesInitializedRef = useRef(false)
  const _selectedFloorIdsRef = useRef<string[]>(selectedFloorIds)

  const _setSelectedTreeNodesBySelectedFloorIds = useCallback(
    (selectedFloorIds: string[]) => {
      const locationTreeNodes = mapLocationTreeContents(regions)
      const selectEachNodeByWorkdayTime = makeSelectEachNodeByFloorWorkdayTime(selectedFloorIds)

      forEachNode(selectEachNodeByWorkdayTime, locationTreeNodes)
      _setSelectedLocationTreeNodesState(locationTreeNodes)
    },
    [regions]
  )

  const setSelectedLocationTreeNodes = (data: LocationTreeNodeT[]) => {
    _selectedFloorIdsRef.current = []

    forEachNode((node: LocationTreeNodeT) => {
      const nodeType = node.nodeData?.nodeType

      if (nodeType === LocationNodeType.Floor && node.nodeData?.floor?.id && node.isSelected) {
        _selectedFloorIdsRef.current.push(node.nodeData?.floor?.id)
      }
    }, data)

    _setSelectedLocationTreeNodesState(data)
  }

  // it's necessary to mutate the state
  const clonedLocationTreeNodes = cloneDeep(selectedLocationTreeNodes)
  // this function is returning the node with given id
  const checkIdsAreSame = (id_1: string, id_2: string) => {
    const newIdStringArray_1 = id_1.split('-')
    const newIdStringArray_2 = id_2.split('-')
    if (newIdStringArray_1.length > 1) newIdStringArray_1.pop()
    if (newIdStringArray_2.length > 1) newIdStringArray_2.pop()
    return newIdStringArray_1.join('') === newIdStringArray_2.join('')
  }
  const findNodeById = (
    selectedLocationTree: LocationTreeNodeT[],
    id: string
  ): LocationTreeNodeT[] => {
    const results: LocationTreeNodeT[] = []

    const searchNodes = (nodes: LocationTreeNodeT[]) => {
      nodes.forEach((node: LocationTreeNodeT) => {
        if (checkIdsAreSame(node.id as string, id)) {
          results.push(node)
        } else if (node.childNodes) {
          searchNodes(node.childNodes)
        }
      })
    }

    searchNodes(selectedLocationTree)
    return results
  }

  const setSelectedLocationContents = (
    nodePath: number[],
    callback: (node: ITreeNode) => void,
    forEachParentCallback?: (node: ITreeNode) => void,
    locationTreeNodes?: LocationTreeNodeT[]
  ): void => {
    // it mutates the clonedLocationTreeNodes object
    const clonedLocationTreeNodes = cloneDeep(locationTreeNodes ?? selectedLocationTreeNodes)

    const node = Tree.nodeFromPath(nodePath, clonedLocationTreeNodes)
    const selectedNode = findNodeById(selectedLocationTreeNodes, node.id as string)

    callback(
      Tree.nodeFromPath(
        selectedNode[0].nodeData ? selectedNode[0].nodeData.nodePath : [],
        selectedLocationTreeNodes
      )
    )

    if (forEachParentCallback) {
      //for cites and buildings
      if (selectedNode[0].childNodes) {
        forEachNode((childNode) => {
          forEachParenNode(
            childNode?.nodeData?.nodePath || [],
            forEachParentCallback,
            selectedLocationTreeNodes,
            //passing true to expand selected node structure
            true
          )
        }, selectedNode[0].childNodes as LocationTreeNodeT[])
      }
      //for floors
      else {
        forEachParenNode(
          selectedNode[0].nodeData ? selectedNode[0].nodeData.nodePath : [],
          forEachParentCallback,
          selectedLocationTreeNodes,
          //passing true to expand selected node structure
          true
        )
      }
    }

    setSelectedLocationTreeNodes(cloneDeep(selectedLocationTreeNodes))
  }

  const setSelectedLocationContentsForEachNode = (
    callback: (
      node: LocationTreeNodeT,
      parentCollection: LocationTreeNodeT[],
      index: number
    ) => void
  ): void => {
    // it mutates the clonedLocationTreeNodes object
    forEachNode(callback, clonedLocationTreeNodes)

    setSelectedLocationTreeNodes(clonedLocationTreeNodes)
  }

  const resetSelectedLocationTreeNodes = () => {
    const selectEachNodeByWorkdayTime = makeSelectEachNodeByFloorWorkdayTime(selectedFloorIds)
    forEachNode(selectEachNodeByWorkdayTime, clonedLocationTreeNodes)
    setSelectedLocationTreeNodes(clonedLocationTreeNodes)
  }

  // init locationTreeNodes states
  useEffect(() => {
    if (isLocationTreeNodesInitializedRef.current || isEmpty(regions)) return

    _setSelectedTreeNodesBySelectedFloorIds(selectedFloorIds)
    isLocationTreeNodesInitializedRef.current = true
  }, [
    regions,
    _setSelectedTreeNodesBySelectedFloorIds,
    selectedFloorIds,
    isLocationTreeNodesInitializedRef,
  ])

  useEffect(() => {
    if (!isEmpty(regions)) {
      _setSelectedTreeNodesBySelectedFloorIds(selectedFloorIds)
    }
  }, [regions, _setSelectedTreeNodesBySelectedFloorIds, selectedFloorIds])

  return {
    selectedLocationTreeNodes,
    resetSelectedLocationTreeNodes,
    setSelectedLocationContents,
    setSelectedLocationContentsForEachNode,
    setSelectedLocationTreeNodes,
    selectedFloorIds: _selectedFloorIdsRef.current,
  }
}

export default useSelectedLocationTreeContents
